import type { ReactNode } from "react";
import { Modal } from "antd";
import {
  ClockCircleOutlined,
  CloseOutlined,
  FireOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useMenuAllergens, useMenuTags } from "../../../../hooks/useMenu";
import type { Item, Allergen, Tag as ItemTag } from "../../../../api/menu";
import {
  getItemName,
  getItemDescription,
  getItemUnitAndSize,
  getTranslation,
} from "../../../../utils/translations";
import { useTranslation } from "react-i18next";
import { ModalHeader, posModalStyles as modalStyles } from "../modalPrimitives";
import { useViewport } from "../../../../hooks/useViewport";
import { RecipeIngredientsSection } from "./RecipeIngredientsSection";
import { AllergenBox, AllergenChip, InfoChip } from "./itemInfoParts";

interface ItemDetailModalProps {
  item: Item | null;
  open: boolean;
  onClose: () => void;
}

// Картинка при счупен адрес — сив квадрат с 📷.
const IMAGE_FALLBACK =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='40'%3E📷%3C/text%3E%3C/svg%3E";

/** Иконките на алергените/таговете идват като base64 без префикс. */
const menuImageSrc = (image: string | null) =>
  image
    ? image.startsWith("data:")
      ? image
      : `data:image/png;base64,${image}`
    : null;

/**
 * „Инфо" за продукт — общ за „Маркиране" и кухненските монитори. Без снимка
 * хедърът е стандартният (икон чип + име + цена); със снимка тя е горе на
 * цялата ширина с бутона за затваряне върху нея, а името и цената са под нея.
 * Тяло: описание · чипове (време, калории, тагове) · „Съставки" (само със
 * склад) · кутия „Алергени"; празните части не се показват.
 */
export const ItemDetailModal = ({
  item,
  open,
  onClose,
}: ItemDetailModalProps) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const allAllergens = useMenuAllergens();
  const allTags = useMenuTags();

  const allergens: Allergen[] = item
    ? allAllergens.filter((allergen) => item.allergenIds.includes(allergen.id))
    : [];
  const tags: ItemTag[] = item
    ? allTags.filter((tag) => item.tagIds.includes(tag.id))
    : [];

  if (!item) return null;

  const itemName = getItemName(item.itemLangs, i18n.language, item.slug);
  const itemDescription = getItemDescription(item.itemLangs, i18n.language);
  const unitAndSize = getItemUnitAndSize(item.itemLangs, i18n.language);
  const price = item.price ? `${item.price.toFixed(2)} €` : undefined;
  // `> 0`, не truthy: 0 минути/калории не е стойност за показване, а
  // `{0 && …}` в JSX би изписало голо „0".
  const hasPrepareTime = (item.prepareTime ?? 0) > 0;
  const hasKcal = (item.kcal ?? 0) > 0;
  const hasChips = hasPrepareTime || hasKcal || tags.length > 0;

  const width = isMobile ? undefined : isTablet ? 520 : 560;

  const title = (
    <>
      {itemName}
      {/* Единица/размер — muted до името, като в продуктовия ред */}
      {unitAndSize && (
        <span className="ml-2 whitespace-nowrap font-pos-mono text-[15px] font-medium text-pos-muted">
          {unitAndSize}
        </span>
      )}
    </>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="flex max-h-[85vh] flex-col bg-pos-card font-pos-sans text-pos-ink">
        {item.images ? (
          <ImageHeader
            imageSrc={item.images}
            imageAlt={itemName}
            title={title}
            price={price}
            onClose={onClose}
            closeLabel={t("common.close")}
            isNarrow={isNarrow}
          />
        ) : (
          <ModalHeader
            icon={<InfoCircleOutlined />}
            title={title}
            subtitle={price}
            onClose={onClose}
            isNarrow={isNarrow}
          />
        )}

        <div
          className={`flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto ${
            isNarrow ? "p-4" : "px-[22px] pt-5 pb-[22px]"
          }`}
        >
          {itemDescription && (
            <p className="m-0 whitespace-pre-line text-[15px] leading-[1.55] text-pos-ink2">
              {itemDescription}
            </p>
          )}

          {hasChips && (
            <div className="flex flex-wrap gap-2">
              {hasPrepareTime && (
                <InfoChip tone="brand" icon={<ClockCircleOutlined />}>
                  {item.prepareTime} {t("common.min")}
                </InfoChip>
              )}
              {hasKcal && (
                <InfoChip tone="brand" icon={<FireOutlined />}>
                  {item.kcal} kcal
                </InfoChip>
              )}
              {tags.map((tag) => (
                <InfoChip key={tag.id} imageSrc={menuImageSrc(tag.image)}>
                  {getTranslation(tag.translations, i18n.language, tag.slug)}
                </InfoChip>
              ))}
            </div>
          )}

          {/* Само в режим със склад — съставките на рецептата (едно ниво) */}
          <RecipeIngredientsSection item={item} open={open} />

          {allergens.length > 0 && (
            <AllergenBox label={t("itemInfo.allergens")}>
              {allergens.map((allergen) => (
                <AllergenChip
                  key={allergen.id}
                  name={getTranslation(
                    allergen.translations,
                    i18n.language,
                    allergen.slug,
                  )}
                  imageSrc={menuImageSrc(allergen.image)}
                />
              ))}
            </AllergenBox>
          )}
        </div>
      </div>
    </Modal>
  );
};

/** Хедърът при снимка: снимката на цялата ширина + бутон за затваряне върху
 * нея, отдолу името и цената (без икон чип). */
const ImageHeader = ({
  imageSrc,
  imageAlt,
  title,
  price,
  onClose,
  closeLabel,
  isNarrow,
}: {
  imageSrc: string;
  imageAlt: string;
  title: ReactNode;
  price?: string;
  onClose: () => void;
  closeLabel: string;
  isNarrow: boolean;
}) => (
  <div className="shrink-0">
    <div className="relative">
      <img
        src={imageSrc}
        alt={imageAlt}
        className={`block w-full bg-pos-canvas object-cover ${
          isNarrow ? "h-[180px]" : "h-[220px]"
        }`}
        onError={(event) => {
          event.currentTarget.src = IMAGE_FALLBACK;
        }}
      />
      <button
        type="button"
        onClick={onClose}
        aria-label={closeLabel}
        className="absolute right-3 top-3 grid h-[38px] w-[38px] cursor-pointer place-items-center rounded-[10px] border border-pos-line2 bg-pos-card text-pos-dim shadow-md transition-colors hover:text-pos-ink"
      >
        <CloseOutlined className="text-[15px]" />
      </button>
    </div>
    <div
      className={`border-b border-pos-line2 ${
        isNarrow ? "px-4 py-3.5" : "px-[22px] py-4"
      }`}
    >
      <h2 className="m-0 text-lg font-extrabold leading-tight text-pos-ink">
        {title}
      </h2>
      {price && (
        <p className="m-0 mt-0.5 text-[13px] font-medium leading-normal text-pos-muted">
          {price}
        </p>
      )}
    </div>
  </div>
);
